import {DOCUMENT, inject} from '@angular/core';
import {Location} from '@angular/common';
import {NavigationError} from '@angular/router';

const RELOAD_STATE_KEY = 'app.stale-chunk-reload';
const RELOAD_COOLDOWN_MS = 30_000;

/** Browser-specific messages thrown when a hashed lazy chunk no longer exists on the server. */
const CHUNK_LOAD_ERROR_PATTERNS: readonly string[] = [
    'Failed to fetch dynamically imported module', // Chrome / Edge
    'error loading dynamically imported module', // Firefox
    'Importing a module script failed', // Safari
];

interface ReloadState {
    url: string;
    at: number;
}

function isStaleChunkError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

function readReloadState(): ReloadState | null {
    try {
        const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
        return raw ? (JSON.parse(raw) as ReloadState) : null;
    } catch {
        return null;
    }
}

function writeReloadState(state: ReloadState): void {
    try {
        sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state));
    } catch {
        // Storage unavailable — proceed without the loop guard.
    }
}

/**
 * Recovers from a redeploy that removed the old hashed lazy chunks:
 * a full page load fetches the fresh index.html (and new chunk map),
 * then boots straight into the originally requested URL.
 */
export function staleChunkNavigationErrorHandler(navError: NavigationError): void {
    if (!isStaleChunkError(navError.error)) {
        // Real error (guard/resolver/module-eval failure) — preserve default behaviour.
        throw navError.error;
    }

    const previous = readReloadState();
    const now = Date.now();
    if (previous !== null && previous.url === navError.url && now - previous.at < RELOAD_COOLDOWN_MS) {
        // Reloaded for this URL moments ago and the chunk is still missing —
        // the deployment itself is broken (or the user is offline). Do not loop.
        console.error('[app] Lazy chunk still missing after reload; auto-recovery aborted.', navError.error);
        return;
    }
    writeReloadState({url: navError.url, at: now});

    console.warn(`[app] Stale lazy chunk detected while navigating to "${navError.url}" — reloading to fetch the new build.`, navError.error);

    const doc = inject(DOCUMENT);
    const location = inject(Location);
    // prepareExternalUrl respects <base href>; navError.url already contains
    // the target path, query params, and fragment.
    doc.location.assign(location.prepareExternalUrl(navError.url));
}
